import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ModalShell } from '@/components/ui';
import { FormField, FormLabel } from '@/components/ui';
import { FormInput } from '@/components/ui';
import { i18nKeys } from '@/i18n/i18nKeyGen';

interface ConfigNameInputModalProps {
  isVisible: boolean;
  onConfirm: (name: string) => void;
  onClose: () => void;
  defaultValue?: string;
}

export const ConfigNameInputModal: React.FC<ConfigNameInputModalProps> = ({
  isVisible,
  onConfirm,
  onClose,
  defaultValue = '',
}) => {
  const { t } = useTranslation('config');
  const [name, setName] = useState(defaultValue);

  React.useEffect(() => {
    if (isVisible) {
      setName(defaultValue);
    }
  }, [isVisible, defaultValue]);

  const handleConfirm = () => {
    if (name.trim()) {
      onConfirm(name.trim());
      setName('');
      onClose();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
  };

  return (
    <ModalShell
      isOpen={isVisible}
      title={t(i18nKeys.config.enterConfigName || 'enterConfigName')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t(i18nKeys.config.cancel)}
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={!name.trim()}>
            {t(i18nKeys.config.save || 'save')}
          </Button>
        </>
      }
    >
      <FormField>
        <FormLabel>{t(i18nKeys.config.configName || 'configName')}</FormLabel>
        <FormInput
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t(i18nKeys.config.configNamePlaceholder || 'configNamePlaceholder')}
          autoFocus
        />
      </FormField>
    </ModalShell>
  );
};
